import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import WysiwygOutlinedIcon from '@mui/icons-material/WysiwygOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { Card, IconButton, Stack, SvgIconProps, Tooltip, Typography } from '@mui/material';

import type { PatientIntakeStep } from '@shared/patient-intake-status';

import { ConfirmationModalWrapper } from '../../../../../components/ConfirmationModalWrapper.jsx';
import { getStatusColour } from '../../utils';
import { getStepConfig } from '../../getStepConfig';
import { usePatientIntakeStatusContext } from '../../hooks';
import { StatusIcon } from '../StatusIcon.tsx';
import { CTAButton } from './CTAButton.tsx';
import { ActionMenu } from './ActionMenu.tsx';
import { useActionPromptContext } from './ActionPromptContext.tsx';

const getTitle = (step: PatientIntakeStep) => {
  if (step.stepType === 'BASE_STEP') {
    return step.id.replaceAll('_', ' ');
  }

  return step.title;
};

interface ItemProps extends React.ComponentProps<typeof IconButton> {
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  onClick?: React.ComponentProps<typeof IconButton>['onClick'];
}

const Item = ({ label, Icon, onClick, ...props }: ItemProps) => {
  const handleOnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!onClick) {
      return;
    }

    onClick(e);
  };

  if (!onClick) {
    return null;
  }

  return (
    <IconButton
      className="mini-menu-item"
      onClick={handleOnClick}
      title={label}
      {...props}
      sx={{
        width: '1rem',
        height: '1rem',
        '&:hover': { color: 'primary.main' },
        ...props.sx,
      }}
    >
      <Icon sx={{ fontSize: '0.75rem' }} />
    </IconButton>
  );
};

const HealthCareProviderActionMenuSection = () => {
  const {
    step,
    isDisabled,
    currentStatus,
    isViewOnlyMode,
    onEdit,
    onClick,
    onDelete,
    onShowHistory,
    onToggleDisabled,
  } = useActionPromptContext();

  if (!currentStatus) {
    return null;
  }

  const isCompleted = currentStatus.status === 'COMPLETED';

  const title = getTitle(step);

  return (
    <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end">
      {!isViewOnlyMode && (
        <Item id="update-status-mini-menu-item" label="Update Status" Icon={UpdateOutlinedIcon} onClick={onEdit} />
      )}
      {isCompleted && <Item label="View Completed" Icon={WysiwygOutlinedIcon} onClick={onClick} />}

      {!!onDelete && !isViewOnlyMode && (
        <ConfirmationModalWrapper
          title="Delete Step"
          description={`Are you sure you want to delete the step "${title}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmColour="secondary"
          titleIcon={DeleteIcon}
          onConfirm={onDelete}
        >
          <Item label="Delete" Icon={DeleteIcon} />
        </ConfirmationModalWrapper>
      )}

      {!!onToggleDisabled && !isViewOnlyMode && (
        <ConfirmationModalWrapper
          title={isDisabled ? 'Enable Step' : 'Disable Step'}
          description={`Are you sure you want to ${isDisabled ? 'enable' : 'disable'} the step "${title}"? This means the patient ${isDisabled ? 'will' : 'will not'} be able to see this step in the intake process.`}
          confirmText={isDisabled ? 'Enable' : 'Disable'}
          confirmColour="primary"
          titleIcon={null}
          onConfirm={onToggleDisabled}
        >
          <Item label="Hide" Icon={isDisabled ? VisibilityIcon : VisibilityOffIcon} />
        </ConfirmationModalWrapper>
      )}
      <Item label="View History" Icon={TimelineOutlinedIcon} onClick={onShowHistory} />

      <ActionMenu>
        <Item label="Menu" Icon={MoreVertIcon} onClick={() => {}} />
      </ActionMenu>
    </Stack>
  );
};

const HealthCareProviderTooltipContent = () => {
  const { currentStatus, step } = useActionPromptContext();

  if (!currentStatus) {
    return null;
  }

  const stepConfig = getStepConfig(step.type);

  const status = currentStatus?.status ?? 'NOT_STARTED';
  const statusConfig = stepConfig.statuses[status];
  const { context, label } = statusConfig;

  const stepStatusContext = context({ isHealthcareProvider: true });

  const actionedBy = currentStatus.actionedBy
    ? `Actioned at ${currentStatus.actionedAt?.toDate().toLocaleString() ?? 'an unknown date'} by ${currentStatus?.actionedBy?.name || currentStatus?.actionedBy?.email || 'an unknown user'}`
    : 'No action has been taken on this step yet';

  return (
    <Stack direction="column" gap={0.5} maxWidth="300px">
      <Stack direction="row" gap={0.5} alignItems="center">
        <StatusIcon status={currentStatus.status} stepIdentifier={step.type} sx={{ fontSize: '1rem' }} />
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
      </Stack>
      <Typography variant="caption" fontWeight="bold">
        {actionedBy}
      </Typography>
      <Typography variant="caption">{stepStatusContext}</Typography>
    </Stack>
  );
};

const HealthCareProviderActionSection = () => {
  const { currentStatus, step } = useActionPromptContext();
  const stepConfig = getStepConfig(step.type);

  const status = currentStatus?.status ?? 'NOT_STARTED';

  if (!currentStatus) {
    return null;
  }

  const { label } = stepConfig.statuses[status];

  const { colour } = getStatusColour(status);

  return (
    <Stack direction="column" gap={0.5} alignItems="flex-end" justifyContent="flex-end">
      <Tooltip
        title={<HealthCareProviderTooltipContent />}
        placement="top"
        slots={{ tooltip: Card }}
        slotProps={{ tooltip: { sx: { p: 1 } } }}
      >
        <Typography variant="body2" fontWeight="bold" color={colour}>
          {label}
        </Typography>
      </Tooltip>
      <HealthCareProviderActionMenuSection />
    </Stack>
  );
};

export const ActionSection = () => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();
  const { onClick } = useActionPromptContext();

  const handleOnClick = () => {
    onClick();
  };

  if (!isHealthcareProvider) {
    return <CTAButton onClick={handleOnClick} />;
  }

  return <HealthCareProviderActionSection />;
};
